import { useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { RoutingSkuPolicyRecord, RoutingSkuTargetRecord } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Button, cx } from '../../ui/index.ts';
import type { NamespaceSkuGroup, SkuOption } from '../tenantRoutingShared.ts';
import {
  namespaceMissingSkus,
  skuCoverageLabelKey,
  skuCoverageLampClass,
  skuCoverageState,
} from './skuCoverage.ts';

/**
 * Left column of the config sheet (#1493): namespace → SKU tree.
 *
 * This is the half of the old `NamespacePoolGroup` that scaled with the
 * catalog — namespace header, coverage statistic, missing-SKU list, fill entry
 * point — plus one row per SKU. It renders NO table header and NO policy
 * selector; those moved to the right column and now appear exactly once for
 * the selected SKU instead of once per SKU.
 *
 * Selection is pure client state over data the sheet has already fetched, so
 * moving through this tree issues no request.
 */

/** Marks the SKU rows so arrow-key walking skips the namespace toggles. */
const SKU_OPTION_SELECTOR = '[data-sku-option]';

interface Props {
  readonly groups: readonly NamespaceSkuGroup[];
  readonly collapsedNamespaces: ReadonlySet<string>;
  readonly onToggleNamespace: (channelId: string) => void;
  readonly targetsBySku: ReadonlyMap<string, readonly RoutingSkuTargetRecord[]>;
  readonly policiesBySku: ReadonlyMap<string, RoutingSkuPolicyRecord | null>;
  readonly selectedSkuId: string | null;
  readonly onSelectSku: (sku: SkuOption) => void;
  readonly onPreviewFill: (group: NamespaceSkuGroup) => void;
}

export function TenantRoutingSkuTree({
  groups,
  collapsedNamespaces,
  onToggleNamespace,
  targetsBySku,
  policiesBySku,
  selectedSkuId,
  onSelectSku,
  onPreviewFill,
}: Props): JSX.Element {
  const t = useT();
  const containerRef = useRef<HTMLDivElement | null>(null);

  /**
   * Arrow keys walk the SKU rows only — namespace toggles sit between them in
   * the DOM and Tab already reaches those. Reading the live DOM rather than
   * keeping a ref array keeps this correct across collapse/expand without a
   * second copy of the visibility rule.
   */
  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const container = containerRef.current;
    if (container === null) return;
    const options = [...container.querySelectorAll<HTMLButtonElement>(SKU_OPTION_SELECTOR)];
    const index = options.findIndex((option) => option === event.target);
    if (index < 0) return;
    // Claimed before the bounds check: this container scrolls, so letting the
    // first/last row fall through to the browser default would scroll the
    // column out from under the operator on exactly the two rows where
    // nothing else happens.
    event.preventDefault();
    options[event.key === 'ArrowDown' ? index + 1 : index - 1]?.focus();
  }

  return (
    <div
      ref={containerRef}
      onKeyDown={handleKeyDown}
      className="flex max-h-[70vh] w-[268px] shrink-0 flex-col gap-2 overflow-y-auto"
      data-testid="tenant-routing-sku-tree"
    >
      {groups.map((group) => {
        const { channel } = group;
        const collapsed = collapsedNamespaces.has(channel.id);
        const groupTargetCount = group.skus.reduce(
          (sum, sku) => sum + (targetsBySku.get(sku.skuId)?.length ?? 0),
          0,
        );
        const missing = namespaceMissingSkus(group, targetsBySku, policiesBySku);
        const coveredCount = group.skus.length - missing.length;

        return (
          <div
            key={channel.id}
            className="rounded-lg border border-line-soft bg-well"
            data-testid={`tenant-routing-pool-${channel.id}`}
          >
            <button
              type="button"
              onClick={() => onToggleNamespace(channel.id)}
              aria-expanded={!collapsed}
              className="flex w-full flex-col gap-1 rounded-lg px-3 py-2 text-left transition-colors duration-120 hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
              data-testid={`tenant-routing-pool-toggle-${channel.id}`}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                {collapsed
                  ? <ChevronRight aria-hidden="true" className="h-4 w-4 shrink-0 text-paper-low" />
                  : <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 text-paper-low" />}
                <span className="truncate font-mono text-dense text-paper">{channel.vendor}/{channel.slug}</span>
              </span>
              <span className="flex flex-col gap-0.5 pl-6 font-mono text-micro text-paper-low">
                <span className="truncate">{channel.display_name}</span>
                <span>
                  {t('tenant_routing.coverage.namespace', {
                    covered: String(coveredCount),
                    total: String(group.skus.length),
                    missing: String(missing.length),
                  })}
                </span>
                <span>
                  {t(
                    groupTargetCount === 1
                      ? 'tenant_routing.default_channels.target_count_one'
                      : 'tenant_routing.default_channels.target_count_many',
                    { count: String(groupTargetCount) },
                  )}
                </span>
              </span>
            </button>

            {missing.length > 0 ? (
              <div className="flex flex-col gap-1.5 border-t border-line-soft px-3 py-2">
                <span className="truncate text-micro text-warn" title={missing.map((sku) => sku.skuName).join(', ')}>
                  {missing.map((sku) => sku.skuName).join(', ')}
                </span>
                <Button
                  size="sm"
                  onClick={() => onPreviewFill(group)}
                  data-testid={`tenant-routing-preview-fill-${channel.id}`}
                >
                  {t('tenant_routing.coverage.preview_fill')}
                </Button>
              </div>
            ) : null}

            {collapsed ? null : group.skus.length === 0 ? (
              <p className="m-0 border-t border-line-soft px-3 py-2 text-micro text-paper-low">
                {t('tenant_routing.sku_pools.namespace_empty')}
              </p>
            ) : (
              <div className="flex flex-col border-t border-line-soft py-1">
                {group.skus.map((sku) => (
                  <SkuTreeItem
                    key={sku.skuId}
                    sku={sku}
                    targets={targetsBySku.get(sku.skuId)}
                    selected={sku.skuId === selectedSkuId}
                    onSelect={() => onSelectSku(sku)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SkuTreeItem({
  sku,
  targets,
  selected,
  onSelect,
}: {
  readonly sku: SkuOption;
  readonly targets: readonly RoutingSkuTargetRecord[] | undefined;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): JSX.Element {
  const t = useT();
  const state = skuCoverageState(targets);
  const targetCount = targets?.length ?? 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      data-sku-option=""
      className={cx(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40',
        selected ? 'bg-signal-dim text-paper' : 'text-paper-mid hover:bg-raised hover:text-paper',
      )}
      data-testid={`tenant-routing-sku-item-${sku.skuId}`}
    >
      <span
        aria-hidden="true"
        className={cx('size-1.5 shrink-0 rounded-full', skuCoverageLampClass(state))}
      />
      <span className="min-w-0 flex-1 truncate font-mono text-micro" title={sku.skuName}>
        {sku.model}
      </span>
      <span className="sr-only">{t(skuCoverageLabelKey(state))}</span>
      <span
        className="shrink-0 font-mono text-micro text-paper-low"
        title={t(
          targetCount === 1
            ? 'tenant_routing.default_channels.target_count_one'
            : 'tenant_routing.default_channels.target_count_many',
          { count: String(targetCount) },
        )}
      >
        {targetCount}
      </span>
    </button>
  );
}
